import React, { useState, useEffect } from "react";
import { getApps, initializeApp, deleteApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, updateProfile, signOut, signInWithCustomToken, sendPasswordResetEmail } from "firebase/auth";
import { doc, setDoc, serverTimestamp, collection, onSnapshot } from "firebase/firestore";
import { auth, db, firebaseConfig } from "../config/firebase";
import { FaKey, FaSignInAlt, FaUserPlus, FaEyeSlash, FaEye } from "react-icons/fa";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const UsersPage = () => {
  // Estado para la lista de usuarios
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Estado para crear usuario
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "Cobrador",
  });
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Alerta global
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");

  // Modal cambiar contraseña
  const [passwordModal, setPasswordModal] = useState(null); // { uid, name }
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Auto-login
  const [impersonating, setImpersonating] = useState(null); // uid

  // Cargar usuarios en tiempo real
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setUsers(list);
      setLoadingUsers(false);
    });
    return () => unsub();
  }, []);

  // Auto-ocultar alerta
  useEffect(() => {
    if (alertMessage) {
      const t = setTimeout(() => setAlertMessage(""), 4000);
      return () => clearTimeout(t);
    }
  }, [alertMessage]);

  const showAlert = (type, msg) => {
    setAlertType(type);
    setAlertMessage(msg);
  };

  // --- Crear usuario ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const { name, email, password, role } = newUser;
    if (!name || !email || !password || !role) {
      showAlert("error", "Por favor, completa todos los campos.");
      return;
    }
    setLoading(true);
    setAlertMessage("");

    try {
      let secondaryApp;
      const apps = getApps();
      const secondaryAppName = "Secondary";
      if (apps.find((app) => app.name === secondaryAppName)) {
        secondaryApp = apps.find((app) => app.name === secondaryAppName);
      } else {
        secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
      }

      const secondaryAuth = getAuth(secondaryApp);
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      await updateProfile(userCredential.user, { displayName: name });

      await setDoc(doc(db, "users", userCredential.user.uid), {
        name,
        email,
        role,
        createdAt: serverTimestamp(),
      });

      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);

      showAlert("success", "Usuario registrado correctamente.");
      setNewUser({ name: "", email: "", password: "", role: "Cobrador" });
      setShowCreateForm(false);
    } catch (error) {
      console.error("Error al registrar usuario:", error);
      showAlert("error", "Hubo un error al registrar el usuario.");
    } finally {
      setLoading(false);
    }
  };

  // --- Cambiar contraseña (con timeout para no quedarse colgado) ---
  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      showAlert("error", "La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    if (!backendUrl) {
      showAlert("error", "Backend no configurado. Usa 'Enviar correo de restablecimiento'.");
      return;
    }

    setChangingPassword(true);
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);

      const res = await fetch(`${backendUrl}/api/users/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid: passwordModal.uid, newPassword }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      const data = await res.json();

      if (res.ok) {
        showAlert("success", `Contraseña de ${passwordModal.name} actualizada.`);
        setPasswordModal(null);
        setNewPassword("");
        setShowPassword(false);
      } else {
        showAlert("error", data.error || "Error al cambiar contraseña.");
      }
    } catch (err) {
      console.error("Error al cambiar contraseña:", err);
      if (err.name === "AbortError") {
        showAlert("error", "El servidor no respondió. Usa 'Enviar correo de restablecimiento'.");
      } else {
        showAlert("error", "Error de conexión. Usa 'Enviar correo de restablecimiento'.");
      }
    } finally {
      setChangingPassword(false);
    }
  };

  // --- Enviar correo de restablecimiento (no requiere backend) ---
  const [sendingReset, setSendingReset] = useState(false);
  const handleSendPasswordReset = async () => {
    if (!passwordModal) return;
    const user = users.find((u) => u.id === passwordModal.uid);
    if (!user?.email) {
      showAlert("error", "Este usuario no tiene correo registrado.");
      return;
    }
    setSendingReset(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      showAlert("success", `Correo de restablecimiento enviado a ${user.email}`);
      setPasswordModal(null);
      setNewPassword("");
      setShowPassword(false);
    } catch (err) {
      console.error("Error enviando correo:", err);
      showAlert("error", "Error al enviar correo de restablecimiento.");
    } finally {
      setSendingReset(false);
    }
  };

  // --- Auto-login (impersonate) ---
  const handleImpersonate = async (user) => {
    if (!backendUrl) {
      showAlert("error", "No se ha configurado la URL del backend (REACT_APP_BACKEND_URL).");
      return;
    }

    setImpersonating(user.id);
    try {
      const res = await fetch(`${backendUrl}/api/users/impersonate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid: user.id }),
      });
      const data = await res.json();

      if (!res.ok) {
        showAlert("error", data.error || "Error al generar token.");
        return;
      }

      // Abrir en nueva pestaña: hacer login con el custom token
      const newTab = window.open("", "_blank");
      if (!newTab) {
        showAlert("error", "El navegador bloqueó la ventana emergente. Permite popups para este sitio.");
        return;
      }

      // Crear una app secundaria para el login
      let impersonateApp;
      const apps = getApps();
      const appName = "Impersonate";
      if (apps.find((a) => a.name === appName)) {
        impersonateApp = apps.find((a) => a.name === appName);
      } else {
        impersonateApp = initializeApp(firebaseConfig, appName);
      }

      const impersonateAuth = getAuth(impersonateApp);
      await signInWithCustomToken(impersonateAuth, data.token);

      // Redirect based on role
      const targetPath = user.role === "Cobrador" ? "/cobrador" : "/admin";

      // Write a small HTML page that signs in with the custom token and redirects
      newTab.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Ingresando como ${user.name}...</title>
          <style>
            body { font-family: sans-serif; display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0; background: #f9fafb; }
            .loader { text-align: center; }
            .spinner { border: 4px solid #e5e7eb; border-top: 4px solid #3b82f6; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto 16px; }
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          </style>
        </head>
        <body>
          <div class="loader">
            <div class="spinner"></div>
            <p>Ingresando como <strong>${user.name}</strong>...</p>
          </div>
          <script type="module">
            import { initializeApp } from "https://www.gstatic.com/firebasejs/11.1.0/firebase-app.js";
            import { getAuth, signInWithCustomToken } from "https://www.gstatic.com/firebasejs/11.1.0/firebase-auth.js";

            const firebaseConfig = ${JSON.stringify(firebaseConfig)};
            const app = initializeApp(firebaseConfig);
            const auth = getAuth(app);

            try {
              await signInWithCustomToken(auth, "${data.token}");
              window.location.href = "${targetPath}";
            } catch (err) {
              document.body.innerHTML = '<p style="color:red;text-align:center;margin-top:40vh;">Error al iniciar sesión: ' + err.message + '</p>';
            }
          </script>
        </body>
        </html>
      `);
      newTab.document.close();

      // Cleanup the secondary app
      await signOut(impersonateAuth);
      await deleteApp(impersonateApp);

      showAlert("success", `Sesión de ${user.name} abierta en nueva pestaña.`);
    } catch (err) {
      console.error("Error al hacer impersonate:", err);
      showAlert("error", "Error al iniciar sesión como este usuario.");
    } finally {
      setImpersonating(null);
    }
  };

  // --- Helpers ---
  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-purple-100 text-purple-700",
      Cobrador: "bg-blue-100 text-blue-700",
      Registrador: "bg-amber-100 text-amber-700",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[role] || "bg-gray-100 text-gray-600"}`}>
        {role || "Sin rol"}
      </span>
    );
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Usuarios</h1>
        <button
          onClick={() => setShowCreateForm((prev) => !prev)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
        >
          <FaUserPlus size={14} />
          {showCreateForm ? "Cancelar" : "Nuevo Usuario"}
        </button>
      </div>

      {/* Alerta */}
      {alertMessage && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm font-medium ${
            alertType === "success"
              ? "bg-green-50 border border-green-200 text-green-700"
              : "bg-red-50 border border-red-200 text-red-700"
          }`}
        >
          {alertMessage}
        </div>
      )}

      {/* Formulario crear usuario (colapsable) */}
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
          <h2 className="text-base font-semibold text-gray-700 mb-4">Registrar Nuevo Usuario</h2>
          <form onSubmit={handleFormSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Nombre</label>
              <input type="text" name="name" value={newUser.name} onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
              <input type="email" name="email" value={newUser.email} onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Contraseña</label>
              <input type="password" name="password" value={newUser.password} onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Rol</label>
              <select name="role" value={newUser.role} onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm" required>
                <option value="admin">Admin</option>
                <option value="Cobrador">Cobrador</option>
                <option value="Registrador">Registrador</option>
              </select>
            </div>
            <div className="md:col-span-2 flex justify-end pt-1">
              <button type="submit" disabled={loading}
                className={`px-5 py-2 rounded-lg text-sm font-medium text-white ${
                  loading ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
                } transition-colors`}>
                {loading ? "Registrando..." : "Registrar Usuario"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabla de usuarios */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loadingUsers ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            <span className="ml-3 text-gray-500">Cargando usuarios...</span>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No hay usuarios registrados.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <th className="py-3 px-4 text-left">Nombre</th>
                <th className="py-3 px-4 text-left">Email</th>
                <th className="py-3 px-4 text-center">Rol</th>
                <th className="py-3 px-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4 font-medium text-gray-800">{u.name || "Sin nombre"}</td>
                  <td className="py-3 px-4 text-gray-600">{u.email || "—"}</td>
                  <td className="py-3 px-4 text-center">{getRoleBadge(u.role)}</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => { setPasswordModal({ uid: u.id, name: u.name || u.email }); setNewPassword(""); setShowPassword(false); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                        title="Cambiar contraseña"
                      >
                        <FaKey size={11} />
                        Contraseña
                      </button>
                      {u.role === "Cobrador" && (
                        <button
                          onClick={() => handleImpersonate(u)}
                          disabled={impersonating === u.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors disabled:opacity-50"
                          title="Ingresar como este usuario"
                        >
                          <FaSignInAlt size={11} />
                          {impersonating === u.id ? "Abriendo..." : "Ingresar"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="px-4 py-2.5 bg-gray-50 border-t text-xs text-gray-500">
          {users.length} usuario{users.length !== 1 ? "s" : ""} registrado{users.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Modal Cambiar Contraseña */}
      {passwordModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-gray-800 mb-1">Cambiar Contraseña</h3>
            <p className="text-sm text-gray-500 mb-4">
              Usuario: <span className="font-medium text-gray-700">{passwordModal.name}</span>
            </p>
            <div className="relative mb-4">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nueva contraseña (mín. 6 caracteres)"
                className="w-full border border-gray-300 px-3 py-2.5 rounded-lg text-sm pr-10"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
              </button>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setPasswordModal(null); setNewPassword(""); setShowPassword(false); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={changingPassword || newPassword.length < 6}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors"
                >
                  {changingPassword ? "Cambiando..." : "Cambiar"}
                </button>
              </div>
              <div className="border-t pt-2 mt-1">
                <button
                  onClick={handleSendPasswordReset}
                  disabled={sendingReset}
                  className="w-full px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-50 transition-colors"
                >
                  {sendingReset ? "Enviando..." : "Enviar correo de restablecimiento"}
                </button>
                <p className="text-[10px] text-gray-400 text-center mt-1">No requiere servidor — el usuario recibirá un link por correo</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
